import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useGetFlipgiveToken } from 'common/hooks/useGetAffiliateActivationUrl';
import { WalletCardAdView } from './WalletCardAdView';
import { WalletCardAdPromoView } from './WalletCardAdPromoView';
import { useGetAndCacheTopDealForBrand } from 'common/hooks/useGetAndCacheTopDealForBrand';
import { constructorClient } from 'third-party-integrations/constructor';
import { DealsModal } from 'features/deals/DealsModal';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectMarketingUTMs } from 'common/features/store/duck/utms/selectors';
import { mergeUTMsForAffiliateLink, applyUTMsToURL } from 'utils/utmStorage';
import AffiliateConfirmationModal from 'features/product/components/AffiliateConfirmationModal';
import { getConfirmationDrawerText } from 'common/utils/confirmationDrawer';
import { logViewWalletAd, logClickWalletAd, logFGDealClick } from 'config/analytics';
import { selectSelectedOrgId } from 'common/features/store/duck/home/duck';
import { selectWalletAdByBrandId } from 'common/features/store/duck/wallet-ads/duck';
import {
    selectIsFlipgiveEnabledWeb,
    selectIsConstructorBrowseEnabledWeb,
} from 'common/features/store/duck/ffs';
import { useIsInViewport } from 'hooks/useIsInViewport';

export type WalletAdLocation = 'wallet_view' | 'gift_card_details';

/**
 * Minimal brand info needed to display wallet ads.
 * Works with both ECommBrand and the full Brand type.
 */
export interface WalletAdBrand {
    id: number | string;
    name: string;
    imageUrl: string;
}

interface WalletCardAdProps {
    cardBrand: WalletAdBrand;
    location: WalletAdLocation;
}

/**
 * WalletCardAd Connected component to display the ad for a given CardBrand
 * Supports both affiliate brands (with deals) and gift card only brands (with promotional text)
 */
export const WalletCardAd = ({ cardBrand, location }: WalletCardAdProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [confirmationModalShown, setConfirmationModalShown] = useState(false);
    const hasLoggedView = useRef(false);
    const adRef = useRef<HTMLDivElement>(null);
    const isAdInViewport = useIsInViewport(adRef);
    const { token } = useGetFlipgiveToken();
    const isFlipgiveEnabled = useRootSelector(selectIsFlipgiveEnabledWeb);
    const isConstructorAffiliateEnabled = useRootSelector(selectIsConstructorBrowseEnabledWeb);
    const { deal, isLoading } = useGetAndCacheTopDealForBrand(
        constructorClient,
        cardBrand,
        token,
        isFlipgiveEnabled,
        isConstructorAffiliateEnabled
    );
    const marketingUTMs = useRootSelector(selectMarketingUTMs) || {};
    const orgId = useRootSelector(selectSelectedOrgId);

    // Get Contentful wallet ad for promotional text (gift card only brands)
    const walletAdSelector = useRootSelector(selectWalletAdByBrandId);
    const walletAd = walletAdSelector(cardBrand.id);
    const promotionalText = walletAd?.promotionalText;

    const brand = deal?.brand;

    // Determine if we have content to show
    // Only show promotional text if loading is complete AND there's no deal
    const hasAffiliateDeal = !isLoading && deal;
    const hasPromoText = !isLoading && !deal && promotionalText;
    const hasContent = hasAffiliateDeal || hasPromoText;

    // Log view_wallet_ad when ad is displayed and visible in viewport
    useEffect(() => {
        if (hasContent && isAdInViewport && !hasLoggedView.current) {
            hasLoggedView.current = true;
            const eventParams = {
                location,
                brandId: String(brand?.id || cardBrand.id),
                brandName: brand?.name || cardBrand.name,
                orgId,
            };

            logViewWalletAd(eventParams);
        }
    }, [hasContent, isAdInViewport, brand, location, cardBrand.id, cardBrand.name, orgId]);

    const handleCtaClick = useCallback(
        (url: string) => {
            if (!url || !deal) {
                return;
            }

            // click_wallet_ad event
            const clickWalletAdParams = {
                location,
                brandId: String(brand?.id || cardBrand.id),
                brandName: brand?.name || cardBrand.name,
                orgId,
            };

            logClickWalletAd(clickWalletAdParams);

            // affiliate_click event
            const dealType = (deal.dealType || 'deal') as 'deal' | 'coupon';
            const version = `react-web-${process.env.REACT_APP_VERSION}`;

            const affiliateClickParams = {
                dealType,
                brandId: String(brand?.id || cardBrand.id),
                brandName: brand?.name || cardBrand.name,
                version,
                location: 'wallet_ad' as const,
                orgId,
                affiliateMobilePlatform: 'web' as const,
            };

            logFGDealClick(affiliateClickParams);

            // Apply UTMs based on location
            const utmSource =
                location === 'wallet_view' ? 'wallet_deal_ad' : 'gift_card_details_ad';
            const productUTMs = {
                utm_channel: 'web',
                utm_source: utmSource,
                utm_content: deal.dealType || 'deal',
            };

            const mergedUTMs = mergeUTMsForAffiliateLink(marketingUTMs, productUTMs);
            const urlWithUTMs = applyUTMsToURL(url, mergedUTMs);

            // Show confirmation modal and open URL
            setConfirmationModalShown(true);
            window.open(urlWithUTMs, '_blank', 'noopener,noreferrer');
        },
        [location, deal, brand, cardBrand.id, cardBrand.name, marketingUTMs, orgId]
    );

    // Don't render if no content (no deal and no promotional text)
    // But we still need to attach the ref for visibility detection, so we render a hidden div
    if (!hasContent) {
        return <div ref={adRef} style={{ display: 'none' }} />;
    }

    // Render promotional text for gift card only brands
    if (hasPromoText) {
        return (
            <div ref={adRef} className="w-full">
                <WalletCardAdPromoView
                    promotionalText={promotionalText}
                    variant={location === 'gift_card_details' ? 'card' : 'inline'}
                />
            </div>
        );
    }

    // Render affiliate deal ad
    return (
        <>
            <div ref={adRef} className="w-full">
                <WalletCardAdView
                    deal={deal!}
                    clickDetails={() => setIsModalOpen(true)}
                    location={location}
                    onCtaClick={handleCtaClick}
                />
            </div>

            <DealsModal
                isOpen={isModalOpen && !confirmationModalShown}
                onClose={() => setIsModalOpen(false)}
                dealWithBrand={deal!}
                location={location === 'wallet_view' ? 'wallet_deal_ad' : 'gift_card_details_ad'}
            />

            {brand && brand?.imageUrl && (
                <AffiliateConfirmationModal
                    brandLogo={brand?.imageUrl}
                    earningsPercentage={`${brand?.affiliateVariablePricingText ? 'Up to ' : ''}${
                        brand?.isOnBonus && brand?.affiliateBonus
                            ? brand?.affiliateBonus
                            : brand?.affiliateRebate
                    }%`}
                    brandName={brand.name}
                    timeframeMessage={getConfirmationDrawerText(
                        brand?.averagePurchaseDelayHours || 0
                    )}
                    isOpen={confirmationModalShown}
                    onClose={() => {
                        setConfirmationModalShown(false);
                    }}
                />
            )}
        </>
    );
};
